/**
 * AIReviewTab — generates a grounded manager review draft via the proxy.
 *
 * Helper components live in AIReviewTabHelpers.tsx.
 * Prompt construction lives in buildReviewPrompt.ts.
 */
import React, { useState } from 'react';
import { C } from '../../data/managerReviewData';
import { authenticatedFetch } from '../../../../shared/utils/authenticatedFetch';
import type { Employee, BehaviorData, Deliverable, FeedbackItem } from '../../types/managerReview';
import {
  QualityDisplay, CARatingField, CATextField, CAPromotionField,
} from './AIReviewTabHelpers';
import { buildReviewPrompt } from './buildReviewPrompt';
import { parseOutput } from './reviewOutputTypes';
export type { ParsedOutput } from './reviewOutputTypes';
import type { ParsedOutput } from './reviewOutputTypes';

const PROXY_URL =
  import.meta.env.VITE_PROXY_URL ||
  'https://proxy-prod-520260363689.europe-west3.run.app';

interface AIReviewTabProps {
  employee: Employee;
  level: string;
  behaviorsData: Record<string, BehaviorData>;
  behaviorsRating: number | null;
  behaviorsRatingNote: string;
  deliverables: Deliverable[];
  deliveryRating: number | null;
  overallRating: number | null;
  overallRatingNote: string;
  promotion: 'yes' | 'no' | null;
  promotionNote: string;
  biasNote: string;
  selfReview: string;
  perfFeedbackRaw?: string;
  anytimeFeedbackRaw?: string;
  feedbackItems: FeedbackItem[];
  generatedReview: ParsedOutput | null;
  setGeneratedReview: React.Dispatch<React.SetStateAction<ParsedOutput | null>>;
  onNavigate?: (tab: number, anchor?: string) => void;
  onOpenFeedback?: () => void;
}

export default function AIReviewTab({
  employee, level, behaviorsData, behaviorsRating, behaviorsRatingNote,
  deliverables, deliveryRating, overallRating, overallRatingNote,
  promotion, promotionNote, biasNote, selfReview, perfFeedbackRaw,
  anytimeFeedbackRaw, feedbackItems,
  generatedReview, setGeneratedReview, onNavigate, onOpenFeedback,
}: AIReviewTabProps) {
  const [loading, setLoading] = useState(false);
  const [loadingField, setLoadingField] = useState<'delivery' | 'behaviors' | null>(null);
  const [refiningField, setRefiningField] = useState<'delivery' | 'behaviors' | null>(null);
  const [error, setError] = useState('');
  const [editedFields, setEditedFields] = useState<Set<'delivery' | 'behaviors'>>(new Set());
  const [confirmRegenerate, setConfirmRegenerate] = useState(false);

  const markEdited = (field: 'delivery' | 'behaviors') =>
    setEditedFields(prev => new Set([...prev, field]));

  const parsed = generatedReview;
  const setParsed = setGeneratedReview;

  const promptData = {
    employee, level, behaviorsData, behaviorsRating, behaviorsRatingNote,
    deliverables, deliveryRating, overallRating, overallRatingNote,
    promotion, promotionNote, biasNote, selfReview, perfFeedbackRaw,
    anytimeFeedbackRaw, feedbackItems,
  };

  const generate = async () => {
    setConfirmRegenerate(false);
    setEditedFields(new Set());
    setLoading(true);
    setParsed(null);
    setError('');
    try {
      const res = await authenticatedFetch(`${PROXY_URL}/api/people/manager-review/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prompt: buildReviewPrompt(promptData, 'full') }),
      });
      const data = await res.json() as { text?: string; error?: string };
      if (!res.ok) setError(data.error || `Request failed with status ${res.status}`);
      else setParsed(parseOutput(data.text || ''));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
    setLoading(false);
  };

  const regenerateField = async (field: 'delivery' | 'behaviors') => {
    setEditedFields(prev => { const s = new Set(prev); s.delete(field); return s; });
    setLoadingField(field);
    try {
      const res = await authenticatedFetch(`${PROXY_URL}/api/people/manager-review/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prompt: buildReviewPrompt(promptData, field) }),
      });
      const data = await res.json() as { text?: string; error?: string };
      if (!res.ok) { setError(data.error || `Request failed with status ${res.status}`); }
      else {
        const text = data.text || '';
        if (field === 'delivery') {
          const i = text.indexOf('### FIELD 3:');
          const content = i !== -1 ? text.slice(i + '### FIELD 3:'.length).trim() : text.trim();
          setParsed(p => p ? { ...p, deliverables: content } : p);
        } else {
          const i = text.indexOf('### FIELD 5:');
          const content = i !== -1 ? text.slice(i + '### FIELD 5:'.length).trim() : text.trim();
          setParsed(p => p ? { ...p, behaviors: content } : p);
        }
      }
    } catch (e) { setError(e instanceof Error ? e.message : String(e)); }
    setLoadingField(null);
  };

  const refineField = async (
    field: 'delivery' | 'behaviors',
    currentText: string,
    instruction: string,
  ): Promise<{ ok: true } | { ok: false; message: string }> => {
    setRefiningField(field);
    try {
      const mode = field === 'delivery' ? 'refine-delivery' : 'refine-behaviors';
      const res = await authenticatedFetch(`${PROXY_URL}/api/people/manager-review/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prompt: buildReviewPrompt(promptData, mode, { currentText, instruction }) }),
      });
      const data = await res.json() as { text?: string; error?: string };
      if (!res.ok) { setRefiningField(null); return { ok: false, message: data.error || `Request failed with status ${res.status}` }; }
      const text = (data.text || '').trim();
      if (text.startsWith('RULES_VIOLATION:')) { setRefiningField(null); return { ok: false, message: text.replace(/^RULES_VIOLATION:\s*/i, '') }; }
      if (field === 'delivery') setParsed(p => p ? { ...p, deliverables: text } : p);
      else setParsed(p => p ? { ...p, behaviors: text } : p);
      markEdited(field);
      setRefiningField(null);
      return { ok: true };
    } catch (e) {
      setRefiningField(null);
      return { ok: false, message: e instanceof Error ? e.message : 'Refinement failed' };
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>

      {/* Header */}
      <div style={{ background: C.card, borderRadius: 10, padding: 24, boxShadow: '0 2px 10px rgba(0,0,0,0.07)', border: `1px solid ${C.border}` }}>
        <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 6 }}>🤖 AI Review Generator</div>
        <p style={{ color: C.muted, fontSize: 13, lineHeight: 1.6, margin: '0 0 16px' }}>
          Analyses your inputs, flags gaps and inconsistencies, then generates the ready-to-copy Culture Amp fields.
          Everything is grounded in the talent framework, rating scale, and your notes — nothing is invented.
        </p>
        {!confirmRegenerate ? (
          <button
            onClick={() => {
              if (editedFields.size > 0) { setConfirmRegenerate(true); }
              else { void generate(); }
            }}
            disabled={loading}
            style={{
              fontFamily: 'Inter', fontWeight: 700, fontSize: 14, border: 'none',
              cursor: loading ? 'not-allowed' : 'pointer', borderRadius: 8,
              padding: '10px 22px', display: 'inline-flex', alignItems: 'center', gap: 8,
              opacity: loading ? 0.6 : 1, background: C.blue, color: '#fff',
            }}
          >
            {loading ? (
              <>
                <span style={{ width: 14, height: 14, borderRadius: '50%', border: '2px solid rgba(255,255,255,0.4)', borderTopColor: '#fff', animation: 'spin 0.8s linear infinite', display: 'inline-block' }} />
                Generating…
              </>
            ) : parsed ? '↺ Regenerate AI Review' : '✨ Generate AI Review'}
          </button>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 13, color: '#92400e' }}>
              ⚠️ Your manual edits to{' '}
              <strong>
                {[editedFields.has('delivery') ? 'Field 3' : null, editedFields.has('behaviors') ? 'Field 5' : null]
                  .filter(Boolean).join(' and ')}
              </strong>
              {' '}will be lost.
            </span>
            <button
              onClick={() => { void generate(); }}
              style={{
                fontFamily: 'Inter', fontWeight: 700, fontSize: 13, border: 'none',
                cursor: 'pointer', borderRadius: 7, padding: '7px 16px',
                background: C.blue, color: '#fff',
              }}
            >Regenerate anyway</button>
            <button
              onClick={() => setConfirmRegenerate(false)}
              style={{
                fontFamily: 'Inter', fontWeight: 600, fontSize: 13,
                cursor: 'pointer', borderRadius: 7, padding: '7px 14px',
                background: 'transparent', border: `1px solid ${C.border}`, color: C.muted,
              }}
            >Cancel</button>
          </div>
        )}
        {error && (
          <div style={{ marginTop: 14, padding: '10px 14px', background: 'rgba(232,41,74,0.06)', border: '1px solid rgba(232,41,74,0.3)', borderRadius: 8, fontSize: 13, color: '#c0392b' }}>
            ⚠️ {error}
          </div>
        )}
      </div>

      {/* Loading */}
      {loading && (
        <div style={{ background: C.card, borderRadius: 10, padding: 24, boxShadow: '0 2px 10px rgba(0,0,0,0.07)', border: `1px solid ${C.border}` }}>
          {['Scoring review completeness…', 'Checking for gaps and inconsistencies…', 'Scanning for potential bias…', 'Drafting Culture Amp fields…'].map((msg, i, arr) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0', borderBottom: i < arr.length - 1 ? `1px solid ${C.border}` : 'none' }}>
              <div style={{ width: 16, height: 16, borderRadius: '50%', flexShrink: 0, border: `2px solid ${C.blue}`, borderTopColor: 'transparent', animation: 'spin 0.8s linear infinite' }} />
              <span style={{ fontSize: 13, color: C.muted }}>{msg}</span>
            </div>
          ))}
        </div>
      )}

      {/* Results */}
      {parsed && !loading && (
        <>
          <div style={{ background: C.card, borderRadius: 10, padding: 24, boxShadow: '0 2px 10px rgba(0,0,0,0.07)', border: `1px solid ${C.border}` }}>
            <div style={{ fontWeight: 700, fontSize: 14, color: C.text, marginBottom: 16 }}>📊 Review Quality</div>
            <QualityDisplay
              raw={parsed.quality}
              hasInsufficientData={
                parsed.deliverables.includes('[INSUFFICIENT DATA') ||
                parsed.behaviors.includes('[INSUFFICIENT DATA')
              }
              onNavigate={onNavigate}
              onOpenFeedback={onOpenFeedback}
            />
          </div>
          <div style={{ background: C.card, borderRadius: 10, padding: 24, boxShadow: '0 2px 10px rgba(0,0,0,0.07)', border: `1px solid ${C.border}` }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 4, flexWrap: 'wrap' }}>
              <div style={{ fontWeight: 700, fontSize: 16, color: C.text }}>📋 Culture Amp — Ready to Copy</div>
              <a
                href="https://finn.eu.cultureamp.com/app/home"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 5,
                  fontSize: 12, fontWeight: 600, color: C.blue,
                  border: `1px solid ${C.blue}`, borderRadius: 6,
                  padding: '5px 12px', textDecoration: 'none',
                  background: C.blue + '0d', whiteSpace: 'nowrap',
                  fontFamily: 'Inter',
                }}
              >Open Culture Amp ↗</a>
            </div>
            <div style={{ fontSize: 12, color: C.muted, marginBottom: 20, lineHeight: 1.5 }}>
              Edit the text fields as needed, then copy each one manually into Culture Amp.
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <CARatingField num={1} label="Overall employee performance" rating={overallRating} />
              <CARatingField num={2} label="Delivery Rating" rating={deliveryRating} />
              <CATextField
                num={3} label="Delivery Feedback"
                hint="Please provide an overview of the employee's key Deliverables over the past 6 months, noting for each the impact achieved, strengths demonstrated, and opportunities for improvement."
                value={parsed.deliverables}
                onEdit={v => { markEdited('delivery'); setParsed(p => p ? { ...p, deliverables: v } : p); }}
                onRegenerate={() => { void regenerateField('delivery'); }}
                regenerating={loadingField === 'delivery'}
                onRefine={(instruction) => refineField('delivery', parsed?.deliverables ?? '', instruction)}
                refining={refiningField === 'delivery'}
              />
              <CARatingField num={4} label="Behavior Rating" rating={behaviorsRating} />
              <CATextField
                num={5} label="Behaviors Feedback"
                hint="Please provide examples of situations where the employee demonstrated our expected Behaviors in line with their Talent Framework, and where could they have been stronger?"
                value={parsed.behaviors}
                onEdit={v => { markEdited('behaviors'); setParsed(p => p ? { ...p, behaviors: v } : p); }}
                onRegenerate={() => { void regenerateField('behaviors'); }}
                regenerating={loadingField === 'behaviors'}
                onRefine={(instruction) => refineField('behaviors', parsed?.behaviors ?? '', instruction)}
                refining={refiningField === 'behaviors'}
              />
              <CAPromotionField promotion={promotion} />
            </div>
          </div>
        </>
      )}

      {/* Empty state */}
      {!parsed && !loading && !error && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {[{ icon: '📊', label: 'Review Quality' }, { icon: '📋', label: 'Culture Amp — Ready to Submit' }].map(s => (
            <div key={s.label} style={{ background: C.card, borderRadius: 10, padding: 24, boxShadow: '0 2px 10px rgba(0,0,0,0.07)', border: `1px solid ${C.border}`, opacity: 0.4 }}>
              <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 12, color: C.muted }}>{s.icon} {s.label}</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {[95, 75, 50].map((w, i) => (
                  <div key={i} style={{ height: 11, background: C.border, borderRadius: 4, width: `${w}%` }} />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
